import { useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "@/hooks/useTranslation";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { useUserExperiences, UserExperience } from "@/hooks/useUserExperiences";
import { ExperienceForm } from "./ExperienceForm";
import { 
  Edit3, Plus, Building2, Calendar, MapPin, 
  Briefcase, Trash2
} from "lucide-react";


interface ExperienceSectionProps {
  userId: string;
  isEditing: boolean;
}

export const ExperienceSection = ({ userId, isEditing }: ExperienceSectionProps) => {
  const { t, i18n } = useTranslation('ui');
  const toast = useTranslatedToast();
  const { experiences, loading, addExperience, updateExperience, deleteExperience } = useUserExperiences(userId);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  const [formData, setFormData] = useState({
    company: "",
    position: "",
    location: "",
    employment_type: "Full-time",
    start_date: "",
    end_date: "",
    is_current: false,
    description: ""
  });

  const handleSave = useCallback(async (experienceId?: string) => {
    try {
      // Prepare data with proper date handling
      const dataToSave = {
        ...formData,
        start_date: formData.start_date || null,
        end_date: formData.is_current ? null : (formData.end_date || null),
        location: formData.location || null,
        description: formData.description || null
      };

      if (experienceId) {
        // Update existing
        await updateExperience(experienceId, dataToSave);
      } else {
        // Create new
        await addExperience(dataToSave);
        setShowAddForm(false);
      }

      setEditingItem(null);
      resetForm();
      toast.success('success.updated');
    } catch (err) {
      console.error('Error saving experience:', err);
      toast.error('error.failed_to_save');
    }
  }, [formData, updateExperience, addExperience, toast]);

  const handleDelete = useCallback(async (experienceId: string) => {
    try {
      await deleteExperience(experienceId);
      toast.success('success.deleted');
    } catch (err) {
      console.error('Error deleting experience:', err);
      toast.error('error.generic');
    }
  }, [deleteExperience, toast]);

  const resetForm = useCallback(() => {
    setFormData({
      company: "",
      position: "",
      location: "",
      employment_type: "Full-time",
      start_date: "",
      end_date: "",
      is_current: false,
      description: ""
    });
  }, []);

  const loadFormData = useCallback((experience: UserExperience) => {
    setFormData({
      company: experience.company,
      position: experience.position,
      location: experience.location || "",
      employment_type: experience.employment_type || "Full-time",
      start_date: experience.start_date || "",
      end_date: experience.end_date || "",
      is_current: experience.is_current,
      description: experience.description || ""
    });
  }, []);

  const handleFormDataChange = useCallback((field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleCancel = useCallback(() => {
    setEditingItem(null);
    setShowAddForm(false);
    resetForm();
  }, [resetForm]);


  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    
    // 根据当前语言设置本地化格式
    const languageMap: { [key: string]: string } = {
      'en': 'en-US',
      'es': 'es-ES', 
      'zh': 'zh-CN',
      'fr': 'fr-FR',
      'ja': 'ja-JP'
    };
    
    const locale = languageMap[i18n.language] || 'en-US';
    
    return new Date(dateString).toLocaleDateString(locale, { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  if (loading) {
    return (
      <div className="text-center py-8">{t('profile_sections.loading_experiences')}</div>
    );
  }

  return (
    <div className="space-y-6">
      {isEditing && !showAddForm && (
        <div className="flex justify-end">
          <Button onClick={() => setShowAddForm(true)} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            {t('profile_sections.add_experience')}
          </Button>
        </div>
      )}
        {showAddForm && (
          <ExperienceForm
            formData={formData}
            onFormDataChange={handleFormDataChange}
            onSave={handleSave}
            onCancel={handleCancel}
          />
        )}

        {experiences.length === 0 && !showAddForm ? (
          <div className="py-12 text-center">
            <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t('profile_sections.no_experience_title')}</h3>
            <p className="text-muted-foreground text-sm">
              {t('profile_sections.no_experience_description')}
            </p>
          </div>
        ) : (
          experiences.map((experience) => (
            <div key={experience.id} className="border-b pb-6 last:border-b-0">
              {editingItem === experience.id ? (
                <ExperienceForm
                  formData={formData}
                  onFormDataChange={handleFormDataChange}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  experienceId={experience.id}
                />
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Company name as primary heading */}
                      <h3 className="text-lg font-bold text-foreground mb-1">
                        {experience.company}
                      </h3>
                      
                      {/* Position as secondary text */}
                      <p className="text-base text-muted-foreground mb-3">
                        {experience.position}
                      </p>
                      
                      {/* Tags for employment type, dates, and location */}
                      <div className="flex items-center flex-wrap gap-2 text-xs mb-4">
                        {experience.employment_type && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full"></div>
                            {experience.employment_type}
                          </span>
                        )}
                        <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                          <Calendar className="w-3 h-3" />
                          {formatDate(experience.start_date)} - {
                            experience.is_current ? t('profile_sections.present') : formatDate(experience.end_date)
                          }
                        </span>
                        {experience.location && (
                          <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted text-muted-foreground">
                            <MapPin className="w-3 h-3" />
                            {experience.location}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {isEditing && (
                      <div className="flex gap-2 ml-4">
                        <Button 
                          onClick={() => {
                            loadFormData(experience);
                            setEditingItem(experience.id);
                          }} 
                          size="sm" 
                          variant="ghost"
                        >
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button 
                          onClick={() => handleDelete(experience.id)} 
                          size="sm" 
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Description with proper typography */}
                  {experience.description && (
                    <div className="space-y-2">
                      <p className="text-sm text-foreground leading-relaxed">
                        {experience.description}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
    </div>
  );
};